import { supabase } from '../supabaseClient';
import { LEGAL_RECONSENT_ENABLED } from '../constants/legalConsentPolicy';
import { fetchSignupLegalDocuments } from './signupLegalService';

export type LegalConsentMethod = 'email_signup' | 'google_oauth' | 'existing_user_gate';

function isMissingTableError(message: string | undefined): boolean {
  return /does not exist|schema cache/i.test(message ?? '');
}

/** 약관 updated_at 등 → DB에 넣을 버전 문자열(빈 값은 null) */
function normalizeVersion(value?: string): string | null {
  const s = (value ?? '').trim();
  if (!s) return null;
  const ms = Date.parse(s);
  if (!Number.isNaN(ms)) return new Date(ms).toISOString();
  return s;
}

/**
 * 현재 로그인된 사용자의 동의 기록 존재 여부.
 * 마이그레이션 미적용 환경(does not exist)에서는 게이트를 걸지 않기 위해 true를 반환한다.
 */
export async function hasLegalConsentRecord(): Promise<boolean> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) {
    console.warn('hasLegalConsentRecord getUser:', userError.message);
    return false;
  }
  if (!user) return false;

  const { data, error } = await supabase
    .from('user_legal_consents')
    .select('id')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle();

  if (error) {
    // maybeSingle: 0행이면 data=null·error=null. PGRST116은 혹시 모를 다중행 등
    if (error.code === 'PGRST116') return true;
    if (isMissingTableError(error.message)) return true;
    console.warn('hasLegalConsentRecord:', error.message);
    // 권한·네트워크 오류 시 동의 없이 통과시키지 않음
    return false;
  }
  return data != null && typeof (data as { id?: string }).id === 'string';
}

/**
 * 서비스 이용 가능 여부 판단용 동의 확인.
 * LEGAL_RECONSENT_ENABLED=false(기본)이면 기록 존재 여부만 본다(개정돼도 재동의 없음).
 * true이면 마지막 동의 버전이 현재 문서 버전과 같아야 통과한다.
 */
export async function hasCurrentLegalConsent(): Promise<boolean> {
  const has = await hasLegalConsentRecord();
  if (!has || !LEGAL_RECONSENT_ENABLED) return has;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return false;
  const { data, error } = await supabase
    .from('user_legal_consents')
    .select('terms_version, privacy_version')
    .eq('user_id', user.id)
    .order('consented_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return true;

  let current: { terms_updated_at: string; privacy_updated_at: string };
  try {
    current = await fetchSignupLegalDocuments();
  } catch {
    return true;
  }
  const row = data as { terms_version?: string | null; privacy_version?: string | null };
  const same = (cur: string, saved?: string | null) => {
    const c = normalizeVersion(cur);
    return c == null || c === normalizeVersion(saved ?? undefined);
  };
  return same(current.terms_updated_at, row.terms_version) && same(current.privacy_updated_at, row.privacy_version);
}

export interface RecordLegalConsentParams {
  method: LegalConsentMethod;
  termsVersion?: string;
  privacyVersion?: string;
}

/** 약관/개인정보처리방침 동의 기록 저장(본인만 insert 가능, RLS) */
export async function recordLegalConsent(params: RecordLegalConsentParams): Promise<void> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw new Error(userError.message);
  if (!user) throw new Error('로그인이 필요합니다. 다시 로그인해 주세요.');

  // 이미 있으면 추가 insert 없이 성공 처리(매번 모달 방지)
  const existing = await hasCurrentLegalConsent();
  if (existing) return;

  const { error } = await supabase.from('user_legal_consents').insert({
    user_id: user.id,
    terms_version: normalizeVersion(params.termsVersion),
    privacy_version: normalizeVersion(params.privacyVersion),
    consent_method: params.method,
  });

  if (error) {
    if (isMissingTableError(error.message)) return;
    throw new Error(error.message);
  }

  const saved = await hasCurrentLegalConsent();
  if (!saved) {
    throw new Error('동의 기록이 저장되지 않았습니다. 잠시 후 다시 시도해 주세요.');
  }
}
